// components/private/StepGuard.tsx
import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import { useUser } from "@/context/useUser";
import { useActivePeriod } from "@/hooks/useActivePeriod";
import toast from "react-hot-toast";

const stepRoutes: Record<number, string> = {
    1: "/order/paket",
    2: "/order/konfirmasi",
    3: "/order/pembayaran",
    4: "/order/pembayaran/status",
    5: "/order/kartu",
};

export default function StepGuard({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();

    const { data: user, isLoading: loadingUser } = useUser();
    const { data: activePeriod, isLoading: loadingPeriod } = useActivePeriod();

    // 1. Pastikan data dasar mendarat
    const authReady = !loadingUser && !!user;
    const periodReady = !loadingPeriod && !!activePeriod?.id;

    // Cek apakah user adalah Admin (role_id === 1) agar tidak terjebak loading/redirect
    const isAdmin = useMemo(() => {
        return authReady && user?.role_id === 1;
    }, [authReady, user]);

    // Cek keberadaan participant di dalam user object (mencegah pembacaan properti null)
    const hasParticipantData = !!user?.participant;

    // 2. Identifikasi Status User (PIC vs Mandiri)
    // Gunakan useMemo agar nilainya stabil dan tidak memicu render berlebih
    const isImportedByPic = useMemo(() => {
        // Jika admin, abaikan pengecekan PIC agar bisa bebas akses
        if (isAdmin) return false;
        return authReady && !!user?.participant?.pic_id;
    }, [authReady, user, isAdmin]);

    const participantId = user?.participant_id;
    const periodId = activePeriod?.id;

    // shouldFetch hanya untuk user Mandiri (Bukan Admin dan Bukan PIC)
    const shouldFetchRegistration = useMemo(() => {
        return authReady && hasParticipantData && periodReady && !isImportedByPic && !isAdmin;
    }, [authReady, hasParticipantData, periodReady, isImportedByPic, isAdmin]);

    // 3. Query Registrasi (Hanya untuk Mandiri)
    const {
        data: registrationData,
        isLoading: loadingReg,
        isFetched: regFetched,
        isError,
    } = useQuery({
        queryKey: ["registration-status", participantId, periodId],
        queryFn: async () => {
            const { data } = await axiosClient.get(
                `/participants/${participantId}/registrations/period/${periodId}`
            );
            return data;
        },
        enabled: shouldFetchRegistration,
        retry: false,
        staleTime: 30000,
    });

    // 4. Redirect Logic
    useEffect(() => {
        // Tahan jika data dasar belum siap
        if (!authReady || !periodReady) return;

        // Jika Admin, jangan lakukan redirect flow pendaftaran
        if (isAdmin) return;

        if (!hasParticipantData) {
            toast.error("Akun Anda belum memiliki data peserta. Harap lengkapi Profil Anda terlebih dahulu.");
            navigate("/profile", { replace: true });
            return;
        }

        // --- JALUR PIC ---
        if (isImportedByPic) {
            if (location.pathname !== "/order/imported") {
                navigate("/order/imported", { replace: true });
            }
            return;
        }

        // --- JALUR MANDIRI ---
        // Tunggu loading query registrasi selesai
        if (shouldFetchRegistration && (loadingReg || !regFetched)) return;

        // Belum daftar (Error 404 atau data null)
        if (isError || !registrationData?.data) {
            if (location.pathname !== "/order/paket") {
                navigate("/order/paket", { replace: true });
            }
            return;
        }

        // Sudah daftar, cek step
        const backendStep = registrationData.data.current_step;
        const mustPath = stepRoutes[backendStep];

        if (mustPath && location.pathname !== mustPath) {
            navigate(mustPath, { replace: true });
        }

    }, [
        authReady,
        periodReady,
        isAdmin,
        hasParticipantData,
        isImportedByPic,
        shouldFetchRegistration,
        loadingReg,
        regFetched,
        registrationData,
        isError,
        location.pathname,
        navigate
    ]);

    // 5. BLOCKER UI (Mencegah "Lolos" Tampilan)
    // PENTING: User PIC tidak butuh regFetched untuk render!
    const canRender = useMemo(() => {
        if (!authReady || !periodReady) return false;

        // Admin selalu diperbolehkan render tanpa menunggu data registrasi
        if (isAdmin) return true;

        if (!hasParticipantData) return false;

        if (isImportedByPic) {
            return location.pathname === "/order/imported";
        }

        // Untuk mandiri, harus sudah fetched atau error (belum daftar)
        return regFetched || isError;
    }, [authReady, periodReady, isAdmin, hasParticipantData, isImportedByPic, regFetched, isError, location.pathname]);

    if (!canRender) {
        return (
            <div className="w-full h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 px-4">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-300 border-t-blue-600 mb-4"></div>
                <p className="mt-4 text-sm text-slate-500 animate-pulse">Menyiapkan data ...</p>
            </div>
        );
    }

    return <>{children}</>;
}